import { isPaidSubscription } from '@whispers/sdk';
import dayjs from 'dayjs';
import { useCallback, useRef } from 'react';

import { useInfiniteFeeds } from '../api.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { CardUnlockFeedItem } from '../components/feed/CardUnlockFeedItem.js';
import { FeedEmptyState } from '../components/feed/FeedEmptyState.js';
import { FeedGuideDialog } from '../components/feed/FeedGuideDialog.js';
import { MonthlyPatternFeedItem } from '../components/feed/MonthlyPatternFeedItem.js';
import { NoteFeedItem } from '../components/feed/NoteFeedItem.js';
import { PageFeedItem } from '../components/feed/PageFeedItem.js';
import { ReadingFeedItem } from '../components/feed/ReadingFeedItem.js';
import { CardListSkeleton, CardSkeleton } from '../components/Skeleton.js';
import { StoneCircleCta } from '../components/StoneCircleCta.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver.js';

export const FeedsPage = () => {
  const { user, me, isAuthenticated, isLoading: authLoading } = useAuth();
  const isPaid = isPaidSubscription(user);
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useInfiniteFeeds({ enabled: isAuthenticated && isPaid });
  const sentinelRef = useRef<HTMLDivElement>(null);
  const handleSentinel = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  useIntersectionObserver(sentinelRef, handleSentinel);
  const items = data?.pages.flatMap((page) => page.items) ?? [];
  if (authLoading) return <CardListSkeleton count={2} lines={3} />;
  if (!isAuthenticated)
    return (
      <>
        <Card>
          <div className="text-center">
            <h1 className="text-xl font-medium text-text">Welcome to The Stone Circle</h1>
            <p className="mt-2 text-sm text-text-secondary">
              Sign in to begin a reading and keep track of your tarot journey.
            </p>
            <Button href="/sign-in" className="mt-5 w-full">
              Sign in to begin
            </Button>
          </div>
        </Card>
        <StoneCircleCta />
      </>
    );
  const nextReadingDate = me?.nextReadingDate ? new Date(me.nextReadingDate) : undefined;
  const canStartReading = !nextReadingDate || nextReadingDate <= new Date();
  if (!isPaid)
    return (
      <>
        <ReadingPrompt canStartReading={canStartReading} nextReadingDate={nextReadingDate} isFree />
        <StoneCircleCta />
      </>
    );
  if (isLoading) return <CardListSkeleton count={3} lines={3} />;
  if (isError)
    return (
      <Card>
        <div className="text-center">
          <p className="font-medium text-text">Your feed could not be loaded</p>
          <button
            className="mt-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
            onClick={() => void refetch()}
            type="button"
          >
            Try again
          </button>
        </div>
      </Card>
    );
  if (items.length === 0) return <FeedEmptyState />;
  return (
    <>
      <div>
        <h1 className="text-xl font-medium text-text">Your feed</h1>
        <p className="mt-1 text-sm text-text-secondary">
          A living record of your time in the Stone Circle.
        </p>
        <FeedGuideDialog showsPages={items.some((item) => item.type === 'page')} />
      </div>
      <ReadingPrompt
        canStartReading={canStartReading}
        nextReadingDate={nextReadingDate}
        isFree={false}
      />
      <div className="grid gap-4">
        {items.map((item) => (
          <FeedContent key={item.id} item={item} />
        ))}
        {isFetchingNextPage && <CardSkeleton lines={3} />}
      </div>
      <div ref={sentinelRef} />
    </>
  );
};

type ReadingPromptProps = {
  canStartReading: boolean;
  nextReadingDate: Date | undefined;
  isFree: boolean;
};

const ReadingPrompt = ({ canStartReading, nextReadingDate, isFree }: ReadingPromptProps) => {
  if (!canStartReading && nextReadingDate) {
    return (
      <Card>
        <div className="text-center">
          <h2 className="text-lg font-medium text-text">
            Your next {isFree ? 'complimentary ' : ''}reading is available on{' '}
            {dayjs(nextReadingDate).format('MMMM D, YYYY')}
          </h2>
          {isFree && (
            <p className="mt-2 text-sm text-text-secondary">
              Free members receive one reading every 30 days.
            </p>
          )}
        </div>
      </Card>
    );
  }
  return (
    <Card>
      <div className="text-center">
        <h2 className="text-lg font-medium text-text">
          {isFree ? 'Your complimentary reading is ready' : 'Your next reading is ready'}
        </h2>
        {isFree && (
          <p className="mt-2 text-sm text-text-secondary">
            Free members receive one reading every 30 days.
          </p>
        )}
        <Button href="/spreads" className="mt-5 w-full">
          {isFree ? 'Begin complimentary reading' : 'Begin a new reading'}
        </Button>
      </div>
    </Card>
  );
};

type FeedContentProps = {
  item: NonNullable<ReturnType<typeof useInfiniteFeeds>['data']>['pages'][number]['items'][number];
};

const FeedContent = ({ item }: FeedContentProps) => {
  if (item.type === 'reading') return <ReadingFeedItem item={item} />;
  if (item.type === 'note') return <NoteFeedItem item={item} />;
  if (item.type === 'card_unlock') return <CardUnlockFeedItem item={item} />;
  if (item.type === 'page') return <PageFeedItem item={item} />;
  return <MonthlyPatternFeedItem item={item} />;
};
